import * as React from 'react';
import { Amplitude, Events } from '@iwoca/frontend-tracking-library';
import { IFilterParams, ValueGetterParams } from 'ag-grid-community';
import { ListFilter } from 'components/ListFilter/ListFilter';
import {
  ApplicationTableStateContext,
  TApplicationTableStateContextValue,
} from 'contexts/ApplicationTableStateContext/ApplicationTableStateContext';
import { isEqual, sortBy } from 'lodash';
import { TExternalFilter, TTableFilterName, TToggledValue } from 'types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import {
  getAllFilterValues,
  getFilterSelectItems,
  handleFilterComparison,
  updateTableFilterLocalState,
} from './TableSelectValuesFilter.helpers';
import styles from './TableSelectValuesFilter.module.css';

interface TableFilterProps extends IFilterParams {
  activeExternalFilter: TExternalFilter;
  filterName: TTableFilterName;
  hasPrimeLoans?: boolean;
  labelFormatter?: (value: any) => string;
  loading?: boolean;
  locale: string;
}
type State = {
  selectedValues: any[];
};

export class TableSelectValuesFilter extends React.Component<
  TableFilterProps,
  State
> {
  constructor(props: TableFilterProps) {
    super(props);

    this.state = {
      selectedValues: getAllFilterValues(
        this.props.filterName,
        this.props.hasPrimeLoans,
      ),
    };
  }

  static contextType = ApplicationTableStateContext;

  // required by ag-grid
  isFilterActive = () => {
    return !isEqual(
      sortBy(this.state.selectedValues),
      sortBy(
        getAllFilterValues(this.props.filterName, this.props.hasPrimeLoans),
      ),
    );
  };

  // required by ag-grid
  getModel = () => {
    return { value: this.state.selectedValues };
  };

  // Update the filters by calling internally/externally (required by ag-grid)
  setModel = (model: any) => {
    this.setState({
      selectedValues:
        model?.value ||
        getAllFilterValues(this.props.filterName, this.props.hasPrimeLoans),
    });
  };

  // Comparator function to check filters (required by ag-grid)
  doesFilterPass = (params: ValueGetterParams) => {
    return handleFilterComparison({
      filterName: this.props.filterName,
      selectedValues: this.state.selectedValues,
      params,
      valueGetter: this.props.valueGetter,
    });
  };

  amplitudeTrack = (event: keyof typeof AMPLITUDE_EVENTS, payload?: object) => {
    Amplitude.track(
      Events.introducerPortal.introducerPortalEventCreator({
        event: AMPLITUDE_EVENTS[event],
        locale: this.props.locale,
        payload: payload || {
          'Filter name': this.props.colDef.headerName,
        },
      }),
    );
  };

  // Required by ag-grid
  afterGuiAttached = () => {
    this.amplitudeTrack('tableFilterOpened');
  };

  trackChange = (values: string[], lastToggledValue: TToggledValue | null) => {
    if (!lastToggledValue) return;
    this.amplitudeTrack('tableFilterOptionApplied', {
      'Filter option': lastToggledValue.item,
      'Filter value': lastToggledValue.value,
    });
  };

  handleFilter = (values: string[]) => {
    this.setState({ selectedValues: values }, () => {
      this.props.filterChangedCallback();
      this.closeFilter();
    });
  };

  trackClearFilters = () => {
    this.amplitudeTrack('tableFilterOptionCleared');
  };

  closeFilter = () => {
    const clickEvent = new MouseEvent('mousedown', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    document.body.dispatchEvent(clickEvent);
  };

  render() {
    const context = this.context as TApplicationTableStateContextValue;
    const { hasPrimeLoans, filterName } = this.props;
    return (
      <>
        <ListFilter
          className={styles.tableSelectValuesFilter}
          onChange={this.trackChange}
          onClear={this.trackClearFilters}
          values={
            (context.filters.find((item: any) => item.filter === filterName)
              ?.values as string[]) || undefined
          }
          onSave={(selectedValues) => {
            this.handleFilter(selectedValues);
            updateTableFilterLocalState(
              context,
              filterName,
              selectedValues,
              hasPrimeLoans,
            );
          }}
          onCancel={this.closeFilter}
          items={getFilterSelectItems(filterName, hasPrimeLoans)}
        />
        <button
          data-testid="table-filter-backdrop"
          onClick={this.closeFilter}
          className={styles.backdrop}
        />
      </>
    );
  }
}
